/**
 * Utility functions for managing analysis history
 */

/**
 * Save analysis result to user's history
 * @param {Object} user - User object
 * @param {Object} analysisResult - Analysis result to save
 */
export const saveToHistory = (user, analysisResult) => {
  if (!user || !analysisResult) return;

  console.log('📦 Saving to history:', {
    video_metadata: analysisResult.video_metadata,
    hasMetadata: !!analysisResult.video_metadata,
    metadataKeys: analysisResult.video_metadata ? Object.keys(analysisResult.video_metadata) : []
  });

  // Use consistent user key: id -> email -> username
  const userKey = user.id || user.email || user.username;
  if (!userKey) {
    console.warn('⚠️ No valid user key found, cannot save to history');
    return;
  }
  
  const historyKey = `analysis_history_${userKey}`;
  
  // Get existing history
  const savedHistory = localStorage.getItem(historyKey);
  const history = savedHistory ? JSON.parse(savedHistory) : [];

  // Check if video already exists in history
  const existingIndex = history.findIndex(
    item => item.video_id === analysisResult.video_id
  );

  const historyItem = {
    id: analysisResult.video_id + '_' + Date.now(),
    video_id: analysisResult.video_id,
    videoId: analysisResult.video_id,
    title: analysisResult.video_metadata?.title || analysisResult.title || 'Unknown Video',
    thumbnail: analysisResult.video_metadata?.thumbnail || `https://img.youtube.com/vi/${analysisResult.video_id}/hqdefault.jpg`,
    score: analysisResult.analysis?.gradi_score?.score || 0,
    ratings: analysisResult.analysis?.ratings || {},
    timestamp: new Date().toISOString(),
    analyzedAt: new Date().toISOString(),
    // Save full metadata for dashboard display
    metadata: {
      views: analysisResult.video_metadata?.view_count || 0,
      likes: analysisResult.video_metadata?.like_count || 0,
      comments: analysisResult.video_metadata?.comment_count || 0,
      duration: analysisResult.video_metadata?.duration || 0,
      channel: analysisResult.video_metadata?.channel || 'Unknown',
      upload_date: analysisResult.video_metadata?.upload_date,
      description: analysisResult.video_metadata?.description,
      title: analysisResult.video_metadata?.title
    },
    // Save the full analysis for mind map viewer
    analysis: analysisResult
  };

  console.log('✅ Saved history item:', historyItem);

  if (existingIndex >= 0) {
    // Update existing entry
    history[existingIndex] = historyItem;
  } else {
    // Add new entry at the beginning
    history.unshift(historyItem);
  }

  // Keep only last 100 analyses
  const trimmedHistory = history.slice(0, 100);

  // Save back to localStorage
  localStorage.setItem(historyKey, JSON.stringify(trimmedHistory));

  return trimmedHistory;
};

/**
 * Get user's analysis history
 * @param {Object} user - User object
 * @returns {Array} Analysis history
 */
export const getHistory = (user) => {
  if (!user) return [];

  // Use consistent user key: id -> email -> username
  const userKey = user.id || user.email || user.username;
  if (!userKey) return [];
  
  const historyKey = `analysis_history_${userKey}`;
  const savedHistory = localStorage.getItem(historyKey);
  
  return savedHistory ? JSON.parse(savedHistory) : [];
};

/**
 * Clear user's analysis history
 * @param {Object} user - User object
 */
export const clearHistory = (user) => {
  if (!user) return;

  // Use consistent user key: id -> email -> username
  const userKey = user.id || user.email || user.username;
  if (!userKey) return;
  
  const historyKey = `analysis_history_${userKey}`;
  localStorage.removeItem(historyKey);
};

/**
 * Get user's comment analysis history
 * @param {Object} user - User object
 * @returns {Array} Comment analysis history
 */
export const getCommentAnalysisHistory = (user) => {
  if (!user) return [];

  // Use consistent user key: id -> email -> username
  const userKey = user.id || user.email || user.username;
  if (!userKey) return [];
  
  const historyKey = `comment_analysis_history_${userKey}`;
  const savedHistory = localStorage.getItem(historyKey);
  
  return savedHistory ? JSON.parse(savedHistory) : [];
};

/**
 * Add video to a collection
 * @param {Object} user - User object
 * @param {string} collectionId - Collection ID
 * @param {Object} videoData - Video data to add
 */
export const addToCollection = (user, collectionId, videoData) => {
  if (!user || !collectionId || !videoData) return;

  const userKey = user.id || user.email;
  const collectionsKey = `collections_${userKey}`;
  const saved = localStorage.getItem(collectionsKey);
  const collections = saved ? JSON.parse(saved) : [];

  const updatedCollections = collections.map(collection => {
    if (collection.id === collectionId) {
      const videoExists = collection.videos.some(v => v.video_id === videoData.video_id);
      if (!videoExists) {
        return {
          ...collection,
          videos: [...collection.videos, {
            ...videoData,
            addedAt: new Date().toISOString()
          }],
          updatedAt: new Date().toISOString()
        };
      }
    }
    return collection;
  });

  localStorage.setItem(collectionsKey, JSON.stringify(updatedCollections));
  return updatedCollections;
};

/**
 * Get user's collections
 * @param {Object} user - User object
 * @returns {Array} Collections
 */
export const getCollections = (user) => {
  if (!user) return [];

  const userKey = user.id || user.email;
  const collectionsKey = `collections_${userKey}`;
  const saved = localStorage.getItem(collectionsKey);
  
  return saved ? JSON.parse(saved) : [];
};

